import React, { useState, useEffect, useRef } from 'react';
import SignaturePad from 'signature_pad';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  ClipboardCheck, CheckCircle2, XCircle, MinusCircle, Loader2, ChevronRight,
  AlertTriangle, Camera, MapPin, X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { publicApi } from '../services/api';

/**
 * Public-QR inspection runner.
 *
 *   1. /asset/{code}/inspection-templates → user picks template
 *   2. Fill out a mobile-first form. Renders type-aware inputs per item:
 *        pass_fail | hours | odometer | number | text | select | signature
 *      Plus per-item photo capture (mandatory when item.photo_required),
 *      an overall signature pad, and optional GPS.
 *   3. Submit → backend creates an Inspection with source="public_qr" and
 *      auto-raises Defects (also via_qr) for any "fail" items.
 */

// Mirrors the server-side normaliser so we can short-circuit invalid input
// before hitting the network. Returns canonical "04XX XXX XXX" or null.
const normaliseAuMobile = (raw) => {
  if (!raw) return null;
  let digits = String(raw).replace(/[^0-9+]/g, '');
  if (digits.startsWith('+61')) digits = '0' + digits.slice(3);
  else if (digits.startsWith('61') && digits.length === 11) digits = '0' + digits.slice(2);
  digits = digits.replace(/[^0-9]/g, '');
  if (digits.length !== 10 || !digits.startsWith('04')) return null;
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
};

// Compress an image file before base64 encoding so we don't blow mobile data.
// Resizes the long edge to <=1280px and re-encodes as JPEG at 0.78 quality.
const compressImage = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Could not read file'));
  reader.onload = () => {
    const img = new Image();
    img.onerror = () => reject(new Error('Could not decode image'));
    img.onload = () => {
      const maxEdge = 1280;
      let { width, height } = img;
      if (width > maxEdge || height > maxEdge) {
        if (width >= height) {
          height = Math.round(height * (maxEdge / width));
          width = maxEdge;
        } else {
          width = Math.round(width * (maxEdge / height));
          height = maxEdge;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.78));
    };
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

export const PublicInspectionRunner = ({ code, templates }) => {
  const [activeTemplate, setActiveTemplate] = useState(null);
  if (!Array.isArray(templates) || templates.length === 0) return null;

  return (
    <div className="bg-[#1C1C1C] border border-[#2E2E2E] rounded-lg mb-4" data-testid="public-inspection-runner">
      <div className="p-5 border-b border-[#2E2E2E]">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardCheck className="w-4 h-4 text-[#FFD108]" />
          <h2 className="label-overline">Start an Inspection</h2>
        </div>
        <p className="text-[hsl(0,0%,63%)] text-xs">
          Complete a quick check before using the equipment. Any failures are
          flagged to the operator's workshop in real time.
        </p>
      </div>

      <ul className="divide-y divide-[#2E2E2E]">
        {templates.map(tpl => (
          <li key={tpl.id}>
            <button
              type="button"
              onClick={() => setActiveTemplate(tpl)}
              className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-[#262626] transition-colors"
              data-testid={`public-inspection-start-${tpl.id}`}
            >
              <div className="text-left min-w-0">
                <p className="text-white font-medium text-sm truncate">{tpl.name}</p>
                <p className="text-[11px] text-[hsl(0,0%,63%)] mt-0.5">
                  {tpl.category || 'general'} · {tpl.checklist?.length ?? 0} items
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-[hsl(0,0%,63%)] flex-shrink-0" />
            </button>
          </li>
        ))}
      </ul>

      {activeTemplate && (
        <RunnerDialog
          code={code}
          template={activeTemplate}
          onClose={() => setActiveTemplate(null)}
        />
      )}
    </div>
  );
};

// ─── Single-template runner dialog ──────────────────────────────────────────
const RunnerDialog = ({ code, template, onClose }) => {
  const [reporterName, setReporterName] = useState('');
  const [reporterPhone, setReporterPhone] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const checklist = template.checklist || [];
  const [responses, setResponses] = useState(() =>
    Object.fromEntries(checklist.map(it => [it.id, {
      item_id: it.id,
      result: null,
      value_number: null,
      value_text: '',
      value_select: '',
      comment: '',
      photo: null,
      signature: null,
    }]))
  );
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [gps, setGps] = useState({ lat: null, lng: null, status: 'idle' });

  // Best-effort GPS — request once on mount.
  useEffect(() => {
    if (!navigator.geolocation) {
      setGps((g) => ({ ...g, status: 'unavailable' }));
      return;
    }
    setGps((g) => ({ ...g, status: 'requesting' }));
    navigator.geolocation.getCurrentPosition(
      (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, status: 'ok' }),
      () => setGps({ lat: null, lng: null, status: 'denied' }),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  }, []);

  const updateResponse = (itemId, patch) => {
    setResponses(prev => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  };

  const handlePhotoCapture = async (itemId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      updateResponse(itemId, { photo: dataUrl });
    } catch (err) {
      toast.error('Could not process photo. Try again.');
    } finally {
      // Allow re-selecting the same file.
      e.target.value = '';
    }
  };

  const submit = async () => {
    if (!reporterName.trim()) { toast.error('Enter your name'); return; }
    const phone = normaliseAuMobile(reporterPhone);
    if (!phone) { toast.error('Enter a valid Australian mobile (e.g. 0414 320 847)'); return; }

    // Type-aware completeness validation.
    for (const item of checklist) {
      const r = responses[item.id];
      const t = item.item_type || 'pass_fail';
      const required = item.required !== false;
      if (required) {
        if (t === 'pass_fail' && !r.result) {
          toast.error(`Please answer: ${item.question}`);
          return;
        }
        if ((t === 'hours' || t === 'odometer' || t === 'number') && (r.value_number == null || Number.isNaN(r.value_number))) {
          toast.error(`Enter a value for: ${item.question}`);
          return;
        }
        if (t === 'text' && !(r.value_text || '').trim()) {
          toast.error(`Enter a value for: ${item.question}`);
          return;
        }
        if (t === 'select' && !r.value_select) {
          toast.error(`Choose an option for: ${item.question}`);
          return;
        }
        if (t === 'signature' && !r.signature) {
          toast.error(`Signature required: ${item.question}`);
          return;
        }
      }
      // Photo enforcement is independent of `required`.
      if (item.photo_required && !r.photo) {
        toast.error(`Photo required for: ${item.question}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        template_id: template.id,
        reporter_name: reporterName.trim(),
        reporter_phone: phone,
        reporter_email: reporterEmail.trim() || undefined,
        gps_lat: gps.lat,
        gps_lng: gps.lng,
        responses: Object.values(responses).map(r => ({
          item_id: r.item_id,
          // Send every type-specific field; the backend ignores irrelevant
          // ones per item_type.
          result: r.result || null,
          value_number: r.value_number ?? null,
          value_text: r.value_text || null,
          value_select: r.value_select || null,
          comment: r.comment || null,
          photo: r.photo || null,
          signature: r.signature || null,
        })),
      };
      const res = await publicApi.submitInspection(code, payload);
      setDone(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="public-inspection-runner-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4 text-volt" />
            {template.name}
          </DialogTitle>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center space-y-3" data-testid="public-inspection-done">
            {done.result === 'passed' || done.status === 'passed' ? (
              <>
                <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto" />
                <h3 className="text-lg font-semibold">Inspection passed</h3>
                <p className="text-sm text-muted-foreground">
                  Thanks {reporterName.split(' ')[0]} — everything's been recorded.
                </p>
              </>
            ) : (
              <>
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto" />
                <h3 className="text-lg font-semibold">Inspection failed</h3>
                <p className="text-sm text-muted-foreground">
                  {done.defects_created} defect{done.defects_created === 1 ? '' : 's'} raised.
                  The workshop has been notified.
                </p>
              </>
            )}
            <DialogFooter className="justify-center pt-3">
              <Button onClick={onClose} data-testid="public-inspection-close-btn">Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto pr-1 space-y-4">
              {/* Reporter block */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-2 border-b border-border">
                <div>
                  <Label className="label-overline">Your Name *</Label>
                  <Input
                    value={reporterName}
                    onChange={(e) => setReporterName(e.target.value)}
                    placeholder="Full name"
                    data-testid="public-insp-name-input"
                  />
                </div>
                <div>
                  <Label className="label-overline">Mobile *</Label>
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    value={reporterPhone}
                    onChange={(e) => setReporterPhone(e.target.value)}
                    placeholder="0414 320 847"
                    data-testid="public-insp-phone-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="label-overline">Email (optional)</Label>
                  <Input
                    type="email"
                    value={reporterEmail}
                    onChange={(e) => setReporterEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="public-insp-email-input"
                  />
                </div>
              </div>

              {/* GPS indicator (best-effort, non-blocking) */}
              <div
                className="flex items-center gap-2 text-xs px-1"
                data-testid="public-insp-gps"
              >
                <MapPin className={`w-3.5 h-3.5 ${gps.status === 'ok' ? 'text-emerald-400' : 'text-muted-foreground'}`} />
                {gps.status === 'ok' && (
                  <span className="text-muted-foreground">
                    GPS captured · {gps.lat.toFixed(4)}, {gps.lng.toFixed(4)}
                  </span>
                )}
                {gps.status === 'requesting' && <span className="text-muted-foreground">Locating…</span>}
                {gps.status === 'denied' && <span className="text-muted-foreground">Location declined</span>}
                {gps.status === 'unavailable' && <span className="text-muted-foreground">GPS unavailable</span>}
              </div>

              {/* Checklist */}
              <ol className="space-y-3" data-testid="public-insp-checklist">
                {checklist.map((item, idx) => (
                  <ChecklistItemRow
                    key={item.id}
                    idx={idx}
                    item={item}
                    response={responses[item.id]}
                    onChange={(patch) => updateResponse(item.id, patch)}
                    onPhoto={(e) => handlePhotoCapture(item.id, e)}
                  />
                ))}
              </ol>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="public-insp-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Submit inspection
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Single checklist item ──────────────────────────────────────────────────
const ChecklistItemRow = ({ idx, item, response, onChange, onPhoto }) => {
  const itemType = item.item_type || 'pass_fail';
  const required = item.required !== false;
  const photoRequired = !!item.photo_required;

  return (
    <li
      className="p-3 rounded-md border border-border bg-muted/20 space-y-2"
      data-testid={`public-insp-item-${item.id}`}
    >
      <p className="text-sm">
        <span className="text-muted-foreground mr-1.5">{idx + 1}.</span>
        {item.question}
        {required && <span className="text-red-400 ml-1">*</span>}
      </p>

      {/* Type-aware input */}
      {itemType === 'pass_fail' && (
        <div className="flex flex-wrap gap-2">
          <ResultButton current={response?.result} value="pass" onClick={() => onChange({ result: 'pass' })}>
            <CheckCircle2 className="w-4 h-4" /> Pass
          </ResultButton>
          <ResultButton current={response?.result} value="fail" onClick={() => onChange({ result: 'fail' })}>
            <XCircle className="w-4 h-4" /> Fail
          </ResultButton>
          <ResultButton current={response?.result} value="na" onClick={() => onChange({ result: 'na' })}>
            <MinusCircle className="w-4 h-4" /> N/A
          </ResultButton>
        </div>
      )}

      {(itemType === 'hours' || itemType === 'odometer' || itemType === 'number') && (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={response?.value_number ?? ''}
            onChange={(e) =>
              onChange({ value_number: e.target.value === '' ? null : parseFloat(e.target.value) })
            }
            placeholder={itemType === 'hours' ? 'Hours' : itemType === 'odometer' ? 'Odometer' : 'Value'}
            className="font-mono max-w-[180px] bg-[#262626] border-[#2E2E2E]"
            data-testid={`public-insp-number-${item.id}`}
          />
          <span className="text-xs text-muted-foreground">
            {item.unit || (itemType === 'hours' ? 'hrs' : itemType === 'odometer' ? 'km' : '')}
          </span>
        </div>
      )}

      {itemType === 'text' && (
        <Input
          maxLength={200}
          value={response?.value_text ?? ''}
          onChange={(e) => onChange({ value_text: e.target.value })}
          placeholder="Enter…"
          className="bg-[#262626] border-[#2E2E2E]"
          data-testid={`public-insp-text-${item.id}`}
        />
      )}

      {itemType === 'select' && Array.isArray(item.options) && item.options.length > 0 && (
        <select
          value={response?.value_select || ''}
          onChange={(e) => onChange({ value_select: e.target.value })}
          className="w-full h-10 px-3 rounded-md bg-[#262626] border border-[#2E2E2E] text-sm"
          data-testid={`public-insp-select-${item.id}`}
        >
          <option value="">— Select —</option>
          {item.options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      )}

      {itemType === 'signature' && (
        <PerItemSignaturePad
          itemId={item.id}
          value={response?.signature}
          onChange={(sig) => onChange({ signature: sig })}
        />
      )}

      {/* Comment — relevant when failed or for capture-types (optional) */}
      {(itemType === 'pass_fail'
        ? response?.result === 'fail'
        : itemType !== 'signature') && (
        <Textarea
          value={response?.comment || ''}
          onChange={(e) => onChange({ comment: e.target.value })}
          placeholder={itemType === 'pass_fail' ? "What's wrong with it?" : 'Add a comment (optional)…'}
          rows={2}
          className="bg-[#262626] border-[#2E2E2E]"
          data-testid={`public-insp-comment-${item.id}`}
        />
      )}

      {/* Photo capture — always available; mandatory when photo_required */}
      <div className="pt-1">
        <Label
          className="inline-flex items-center gap-2 cursor-pointer text-xs text-muted-foreground"
          data-testid={`public-insp-photo-label-${item.id}`}
        >
          <span className="inline-flex items-center justify-center w-9 h-9 border border-dashed border-border rounded-md hover:border-primary">
            <Camera className="w-4 h-4" />
          </span>
          <span>
            {response?.photo
              ? 'Photo attached'
              : photoRequired
                ? 'Add photo *'
                : 'Add photo (optional)'}
          </span>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPhoto}
            className="hidden"
            data-testid={`public-insp-photo-input-${item.id}`}
          />
        </Label>
        {response?.photo && (
          <div className="mt-2 relative inline-block">
            <img
              src={response.photo}
              alt="Captured"
              className="rounded-md max-h-32 object-cover border border-border"
            />
            <button
              type="button"
              onClick={() => onChange({ photo: null })}
              className="absolute -top-2 -right-2 bg-black border border-border rounded-full p-0.5 hover:bg-muted"
              data-testid={`public-insp-photo-clear-${item.id}`}
              aria-label="Remove photo"
            >
              <XIcon className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
};

// Per-item signature pad. The component lazily mounts the pad after the
// canvas is in the DOM and lifts the data URL up via onChange.
const PerItemSignaturePad = ({ itemId, value, onChange }) => {
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (canvasRef.current && !padRef.current) {
        padRef.current = new SignaturePad(canvasRef.current, {
          backgroundColor: 'rgb(255,255,255)',
          penColor: 'rgb(0,0,0)',
        });
        padRef.current.addEventListener('endStroke', () => {
          if (padRef.current && !padRef.current.isEmpty()) {
            onChange(padRef.current.toDataURL());
          }
        });
      }
    }, 30);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clear = () => {
    padRef.current?.clear();
    onChange(null);
  };

  return (
    <div className="space-y-2" data-testid={`public-insp-signature-${itemId}`}>
      <div className="bg-white rounded-md p-1">
        <canvas
          ref={canvasRef}
          width={500}
          height={120}
          className="w-full border border-gray-200 rounded touch-none"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={clear}
          data-testid={`public-insp-signature-clear-${itemId}`}
        >
          Clear
        </Button>
        {value && <span className="text-xs text-emerald-400">Signed</span>}
      </div>
    </div>
  );
};

const ResultButton = ({ current, value, onClick, children }) => {
  const isActive = current === value;
  const tone = {
    pass: isActive ? 'bg-emerald-500 text-black border-emerald-500' : 'border-emerald-500/30 text-emerald-400',
    fail: isActive ? 'bg-red-500 text-white border-red-500' : 'border-red-500/30 text-red-400',
    na:   isActive ? 'bg-zinc-500 text-white border-zinc-500' : 'border-zinc-500/40 text-zinc-300',
  }[value];
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border rounded-md inline-flex items-center gap-1 transition-colors ${tone}`}
    >
      {children}
    </button>
  );
};
